import { useEffect, useState, useMemo } from "react";
import { GetPlanListApi } from "../../api/request/license";
import {
  AddLicenseApi,
  EditLicenseApi,
  ChangePlanStatus,
  DeletePlan,
} from "../../api/request/plan";
import ModalComponent from "../../components/global/modals/ModalComponent";
import SkeletonTable from "../../components/table/SkeletonTable";
import planColumns from "../../columns/plan/planColumns";
import { Form, Input, InputNumber, Switch, Select } from "antd";
import moment from "moment";
import Edit from "../../assets/Edit";
import GlobalTooltip from "../../components/common/GlobalTooltip";
import LoadableButton from "../../components/button/LoadableButton";
import Plus from "../../assets/Plus";
import ConfirmationModal from "../../components/global/modals/ConfirmationModal";
import Delete from "../../assets/Delete";

function Plans() {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [form] = Form.useForm();

  // confirmation for status change
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [newStatus, setNewStatus] = useState(null);
  const [isConfirmLoading, setIsConfirmLoading] = useState(false);

  // confirmation for delete
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);

  const [selectedFeatures, setSelectedFeatures] = useState([]);

  // Fetch Plans
  const fetchPlans = async () => {
    try {
      setIsLoading(true);
      const { data } = await GetPlanListApi();
      setData(data?.data || []);
    } catch (error) {
      console.error("Error fetching plan list:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  // Add/Edit
  const handleSubmit = async (values) => {
    setIsLoading(true);
    try {
      const payload = {
        ...values,
        ...(editingPlan
          ? {}
          : { status: values.status ? "active" : "inactive" }),
      };

      if (editingPlan) {
        await EditLicenseApi({ id: editingPlan?.id, ...payload });
        setData((prev) =>
          prev.map((item) =>
            item.id === editingPlan.id ? { ...item, ...payload } : item,
          ),
        );
      } else {
        await AddLicenseApi(payload);
        fetchPlans();
      }

      setIsModalOpen(false);
      form.resetFields();
      setEditingPlan(null);
    } catch (error) {
      console.error("Error saving plan:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Confirm Change Status
  const handleConfirmStatus = async () => {
    if (!selectedPlan) return;
    setIsConfirmLoading(true);
    try {
      await ChangePlanStatus({ id: selectedPlan.id, status: newStatus });
      setData((prev) =>
        prev.map((item) =>
          item.id === selectedPlan.id ? { ...item, status: newStatus } : item,
        ),
      );
      setIsConfirmOpen(false);
      setSelectedPlan(null);
      setNewStatus(null);
    } catch (error) {
      console.error("Error changing status:", error);
    } finally {
      setIsConfirmLoading(false);
    }
  };

  // Confirm Delete
  const handleConfirmDelete = async () => {
    if (!selectedPlan) return;
    setIsDeleteLoading(true);
    try {
      await DeletePlan({ id: selectedPlan.id });
      setData((prev) => prev.filter((item) => item.id !== selectedPlan.id));
      setIsDeleteOpen(false);
      setSelectedPlan(null);
    } catch (error) {
      console.error("Error deleting plan:", error);
    } finally {
      setIsDeleteLoading(false);
    }
  };

  // Open Add
  const openAddModal = () => {
    setEditingPlan(null);
    form.resetFields();
    setIsModalOpen(true);
    setSelectedFeatures([]);
  };

  // Open Edit
  const openEditModal = (plan) => {
    setEditingPlan(plan);
    setSelectedFeatures(plan.features || []);
    form.setFieldsValue({
      name: plan.name,
      total_user: plan.total_user,
      duration: plan.duration,
      amount: plan.amount,
      features: plan.features,
    });
    setIsModalOpen(true);
  };

  const planFeatures = [
    { label: "Select All", value: "SELECT_ALL" },
    { value: "Admin Dashboard", label: "Admin Dashboard" },
    { value: "Single Admin Login", label: "Single Admin Login" },
    {
      value: "Document Upload & Management",
      label: "Document Upload & Management",
    },
    { value: "Folder & File Structure", label: "Folder & File Structure" },
    {
      value: "Year-wise Document Organization",
      label: "Year-wise Document Organization",
    },
    { value: "Search & Filter Reports", label: "Search & Filter Reports" },
    { value: "File Preview & Download", label: "File Preview & Download" },
    { value: "Soft Delete (Recycle Bin)", label: "Soft Delete (Recycle Bin)" },
    { value: "Activity Logs", label: "Activity Logs" },
    { value: "Secure Authentication", label: "Secure Authentication" },
    { value: "Email Notifications", label: "Email Notifications" },
    { value: "Profile Management", label: "Profile Management" },
    { value: "Basic Support", label: "Basic Support" },
    { value: "E-Sign Feature", label: "E-Sign Feature" },
    { value: "Invoice Management", label: "Invoice Management" },
    { value: "Chat Module", label: "Chat Module" },
    { value: "Employee Role Management", label: "Employee Role Management" },
    {
      value: "Permission-based File Sharing",
      label: "Permission-based File Sharing",
    },
    { value: "Task Assignment", label: "Task Assignment" },
    { value: "Audit Logs", label: "Audit Logs" },
    {
      value: "Review & Approval Workflow",
      label: "Review & Approval Workflow",
    },
    { value: "Export Data (PDF / Excel)", label: "Export Data (PDF / Excel)" },
    {
      value: "Service Workflow Management",
      label: "Service Workflow Management",
    },
    { value: "Service File Management", label: "Service File Management" },
    { value: "Client Dashboard", label: "Client Dashboard" },
    { value: "Employee Dashboard", label: "Employee Dashboard" },
  ];

  const columns = useMemo(() => {
    return planColumns.map((col) => {
      if (col.dataIndex === "status") {
        return {
          ...col,
          render: (status, record) => (
            <Switch
              checked={status === "active"}
              checkedChildren="Active"
              unCheckedChildren="Inactive"
              onChange={(checked) => {
                setSelectedPlan(record);
                setNewStatus(checked ? "active" : "inactive");
                setIsConfirmOpen(true);
              }}
            />
          ),
        };
      }
      if (col.dataIndex === "created_at") {
        return {
          ...col,
          render: (date) => (date ? moment(date).format("lll") : "--"),
        };
      }
      if (col.dataIndex === "action") {
        return {
          ...col,
          render: (_, record) => (
            <div className="flex gap-2">
              <GlobalTooltip title="Edit Plan">
                <button onClick={() => openEditModal(record)}>
                  <Edit />
                </button>
              </GlobalTooltip>
              <GlobalTooltip title="Delete Plan">
                <button
                  disabled={isDeleteLoading}
                  onClick={() => {
                    setSelectedPlan(record);
                    setIsDeleteOpen(true);
                  }}
                >
                  <Delete />
                </button>
              </GlobalTooltip>
            </div>
          ),
        };
      }
      return col;
    });
  }, [isDeleteLoading]);

  const allValues = planFeatures
    .filter((f) => f.value !== "SELECT_ALL")
    .map((f) => f.value);

  const handleFeatureChange = (value) => {
    const isSelectAllClicked = value.includes("SELECT_ALL");

    if (isSelectAllClicked) {
      const allSelected = selectedFeatures.length === allValues.length;

      if (allSelected) {
        // Toggle → unselect all
        setSelectedFeatures([]);
        form.setFieldsValue({ features: [] });
      } else {
        // Toggle → select all
        setSelectedFeatures(allValues);
        form.setFieldsValue({ features: allValues });
      }
    } else {
      setSelectedFeatures(value);
      form.setFieldsValue({ features: value });
    }
  };

  return (
    <div>
      <div className="border bg-white rounded-xl py-3">
        <div className="flex items-center justify-between sm:px-4 max-sm:px-2 mb-1">
          <h2 className="text-xl font-semibold">Plans</h2>
          <button
            className="flex items-center gap-2 m-2 px-4 py-2 bg-secondary text-white rounded-md max-sm:px-1 max-sm:py-1"
            onClick={openAddModal}
          >
            <Plus className="w-5 h-5" />
            <span className="max-sm:text-xs">Add Plan</span>
          </button>
        </div>

        {/* Table */}
        <SkeletonTable
          columns={columns}
          data={data}
          isLoading={isLoading}
          pagination={true}
        />
      </div>

      {/* Add/Edit Modal */}
      <ModalComponent
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        title={editingPlan ? "Edit Plan" : "Add Plan"}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ status: true }}
        >
          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              label="Plan Name"
              name="name"
              rules={[{ required: true, message: "Please enter plan name" }]}
            >
              <Input placeholder="Enter plan name" />
            </Form.Item>

            <Form.Item
              label="Total Employees"
              name="total_user"
              rules={[{ required: true, message: "Please enter total users" }]}
            >
              <InputNumber
                min={0}
                className="w-full"
                placeholder="Enter total users"
              />
            </Form.Item>

            <Form.Item
              label="Duration (in months)"
              name="duration"
              rules={[{ required: true, message: "Please enter duration" }]}
            >
              <InputNumber
                min={1}
                className="w-full"
                placeholder="Enter duration in months"
              />
            </Form.Item>

            <Form.Item
              label="Amount"
              name="amount"
              rules={[{ required: true, message: "Please enter amount" }]}
            >
              <InputNumber
                min={0}
                className="w-full"
                placeholder="Enter amount"
              />
            </Form.Item>

            <div className="col-span-2">
              <Form.Item
                label="Plan Features"
                name="features"
                rules={[{ required: true, message: "Please select a type" }]}
              >
                <Select
                  showSearch
                  placeholder="Select features"
                  mode="multiple"
                  maxTagCount="responsive"
                  optionFilterProp="label"
                  value={selectedFeatures}
                  onChange={handleFeatureChange}
                  options={planFeatures}
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </div>
          </div>

          {/* Only for Add */}
          {!editingPlan && (
            <Form.Item label="Status" name="status" valuePropName="checked">
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item>
          )}

          <div className="flex justify-end gap-2 mt-4">
            <LoadableButton
              className="btn-primary"
              type="submit"
              lable={editingPlan ? "Update" : "Add"}
              loadingLable="Saving..."
              isLoading={isLoading}
            />
          </div>
        </Form>
      </ModalComponent>

      {/* Status Change Confirmation */}
      <ConfirmationModal
        isOpen={isConfirmOpen}
        setIsOpen={setIsConfirmOpen}
        type="warning"
        message={`Are you sure you want to set this plan as ${newStatus}?`}
        onConfirm={handleConfirmStatus}
        isLoading={isConfirmLoading}
        loadingLabel="Updating..."
      />

      {/* Delete Confirmation */}
      <ConfirmationModal
        isOpen={isDeleteOpen}
        setIsOpen={setIsDeleteOpen}
        type="error"
        message={`Are you sure you want to delete "${selectedPlan?.name}"?`}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleteLoading}
        loadingLabel="Deleting..."
      />
    </div>
  );
}

export default Plans;
